import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import {
  getArrayLength,
  getHasElementsInArray,
  getUserFullNameOrEmail,
  normalizeEmail,
} from 'utils';
import { useTranslation } from 'react-i18next';
import {
  ShareableLinkAnalyticsFileEntry,
  ShareableLinkAnalyticsMember,
} from 'models/ShareableLink';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import usePastTime from 'hooks/usePastTime';
import useInfinitePagination from 'hooks/useInfinitePagination';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import InfiniteScroll from 'components/InfiniteScroll';
import SecureLinkFile from '../SecureLinkFile';
import SkeletonRows from '../../../SkeletonRows';
import { Loader } from 'ncoded-component-library';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';

import './SecureLinkMember.styles.scss';

const MEMBER_FILES_PAGE_LIMIT = '10';

type SecureLinkMemberProps = {
  className?: string;
  shareableLinkId: string;
  member: ShareableLinkAnalyticsMember;
};

const SecureLinkMember: React.FC<SecureLinkMemberProps> = (props) => {
  const { className, shareableLinkId, member } = props;

  const { _id: memberId, user, visitCount, lastAccessDate } = member || {};

  const [filesContainer, filesContainerRef] = useCallbackRef<HTMLElement>();

  const {
    open,
    setOpen,
    classes: heightAnimationClasses,
  } = useHeightAnimation({ element: filesContainer, defaultOpen: false });

  const requestedAtCount = useRef(-1);

  const classes = classNames('secure-link-member', 'white-panel', className);

  const { t } = useTranslation();

  const lastAccess = usePastTime()(lastAccessDate);

  const {
    items,
    loading,
    shouldContinuePagination,
    onContainerScrolled: onFilesScrolled,
  } = useInfinitePagination<ShareableLinkAnalyticsFileEntry>({
    condition: !!shareableLinkId && !!memberId && !!visitCount && open,
    resetDeps: [shareableLinkId, memberId, open],
    dependencies: [shareableLinkId, memberId],
    uniqueKey: 'dataRoomNode._id',
    makeRequest: (currentPage) =>
      dataRoomApi.shareableLinks
        .getAnalyticsMemberFiles(shareableLinkId, memberId, {
          $page: currentPage.toString(),
          $limit: MEMBER_FILES_PAGE_LIMIT,
        })
        .then(({ data }) => data),
  });

  const loadMoreFiles = useCallback(() => {
    if (
      loading ||
      !shouldContinuePagination ||
      requestedAtCount.current === getArrayLength(items)
    )
      return;

    requestedAtCount.current = getArrayLength(items);
    onFilesScrolled();
  }, [loading, shouldContinuePagination, items, onFilesScrolled]);

  const isFilesLoading = !!visitCount && loading;

  const hasFiles = getHasElementsInArray(items);

  const isInitialFilesLoading = isFilesLoading && !hasFiles;

  return (
    <div className={classes}>
      <div
        className="secure-link-member__user"
        onClick={() => setOpen((old) => !old)}
      >
        <AvatarWithInitials user={user} />
        <div className="name-wrapper">
          <p className="name">{getUserFullNameOrEmail(user)}</p>
          <p>{normalizeEmail(user?.email)}</p>
        </div>

        <div>
          <p className="visits">
            {t(visitCount ? 'linkVisit' : 'dataRoomUC', {
              count: visitCount,
            })}
          </p>
          <p>{t('lastAccessDate', { lastAccess: lastAccess || t('never') })}</p>
        </div>

        <ChevrondownIcon
          className={classNames('chevron-icon', { rotated: open })}
        />
      </div>

      <div
        ref={filesContainerRef}
        className={classNames(
          'secure-link-member__files',
          heightAnimationClasses,
        )}
      >
        {isInitialFilesLoading ? (
          <SkeletonRows />
        ) : hasFiles ? (
          <InfiniteScroll
            localScroll
            className="secure-link-member__files-scroll"
            onScroll={loadMoreFiles}
          >
            {isFilesLoading && <Loader />}

            {items.map((file) => (
              <SecureLinkFile
                key={file.dataRoomNode?._id}
                file={file}
                siblings={items}
                hasMoreSiblings={shouldContinuePagination}
                onLoadMoreSiblings={loadMoreFiles}
              />
            ))}
          </InfiniteScroll>
        ) : (
          <p>{t('noFilesViewed')}</p>
        )}
      </div>
    </div>
  );
};

export default SecureLinkMember;
